import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import {
  Schedule,
  Query,
  QuerySchedulesArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { AppointmentsPageQueryParams } from 'src/components/pages/appointments/appointments.page';
import { CustomPriorityIcon } from 'src/components/pages/appointments/components/appointment-priority-picker';
import { useAppointmentsWhereInputResolver } from 'src/components/pages/appointments/hooks';
import {
  CONTACT_LABEL_PREVIEW_FIELDS,
  mapTaskOrAppointmentEdgesToListItemData,
} from 'src/components/pages/dashboard/components/cards/shared/helpers';
import { TaskOrAppointmentList } from 'src/components/pages/dashboard/components/cards/shared/task-or-appointment-list';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  ChipFilters,
  EventFilterValue,
} from 'src/components/pages/dashboard/components/shared';
import { CORE_PAGE_INFO_FIELDS } from 'src/components/providers/apollo-provider-provider';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useInfiniteListQuery } from 'src/hooks/use-infinite-list-query';
import {
  getLegacyFiltersQueryString,
  useFormatLegacyDateQueryString,
} from 'src/hooks/use-legacy-params-for-dashboard-query';

const PriorityPickerContainer = styled.div`
  & span {
    margin-right: 2px;
  }
`;

export const GET_DASHBOARD_APPOINTMENTS_QUERY = gql`
  ${CORE_PAGE_INFO_FIELDS}
  ${CONTACT_LABEL_PREVIEW_FIELDS}
  # noinspection GraphQLUnresolvedReference,GraphQLSchemaValidation
  query GetAppointments(
    $institutionId: Int!
    $first: Int
    $after: String
    $before: String
    $last: Int
    $where: ScheduleWhereInput
  ) {
    schedules(
      institutionId: $institutionId
      first: $first
      after: $after
      before: $before
      last: $last
      where: $where
      orderBy: { startAt: ASC }
    ) @connection(key: "infinite-scroll") {
      totalCount
      pageInfo {
        ...PageInfoFragment
      }
      edges {
        cursor
        node {
          id
          title
          startAt
          priority
          status
          user {
            id
            fullName
          }
          contact {
            ...ContactLabelPreviewFields
          }
          updatedAt
        }
      }
    }
  }
`;

const PAGE_SIZE = 10;

const BASE_TEST_ID = 'next-appointments-card';

export const NextAppointmentsCard = () => {
  const { t } = useTranslation(['common', 'dashboard', 'appointments']);
  const { id: institutionId } = useCurrentInstitution();
  const whereInputResolver = useAppointmentsWhereInputResolver('custom');
  const handleGraphQLError = useHandleGraphQLError();
  const { targetUserId } = useContext(DashboardFiltersContext);
  const [activeFilter, setActiveFilter] = useState<EventFilterValue>(
    EventFilterValue.TODAY,
  );
  const { endOfDay, startOfToday, initDayJsDate } = useDateHelpers();
  const { formatLegacyDateQueryString } = useFormatLegacyDateQueryString();

  const currentDate = useMemo(() => initDayJsDate(new Date()).toDate(), [initDayJsDate]);

  const getDateFilterValues = useCallback(
    (type: 'QUERY' | 'QUERY-STRING') => {
      const isTypeQuery = type === 'QUERY';

      return activeFilter === EventFilterValue.TODAY
        ? {
            date_from: isTypeQuery
              ? startOfToday().toISOString()
              : formatLegacyDateQueryString(startOfToday()),
            date_to: isTypeQuery
              ? endOfDay(new Date()).toISOString()
              : formatLegacyDateQueryString(endOfDay(new Date())),
          }
        : {
            status: ['RESCHEDULED', 'SCHEDULED'],
            date_from: isTypeQuery
              ? currentDate.toISOString()
              : formatLegacyDateQueryString(currentDate),
          };
    },
    [activeFilter, currentDate, endOfDay, formatLegacyDateQueryString, startOfToday],
  );

  const baseAppointmentListInput: AppointmentsPageQueryParams = useMemo(
    () => ({
      'view-code': 'custom',
      institution_id: institutionId.toString(),
      q: {
        advanced: {
          agent: targetUserId?.toString(),
        },
      },
      table: 'sort|startAt|direction|ASC',
    }),
    [institutionId, targetUserId],
  );

  const appointmentListInput: AppointmentsPageQueryParams = useMemo(
    () => ({
      ...baseAppointmentListInput,
      q: {
        advanced: {
          ...baseAppointmentListInput.q?.advanced,
          ...getDateFilterValues('QUERY'),
        },
      },
    }),
    [baseAppointmentListInput, getDateFilterValues],
  );

  const appointmentListQueryStringInput: AppointmentsPageQueryParams = useMemo(
    () => ({
      ...baseAppointmentListInput,
      q: {
        advanced: {
          ...baseAppointmentListInput.q?.advanced,
          ...getDateFilterValues('QUERY-STRING'),
        },
      },
    }),
    [baseAppointmentListInput, getDateFilterValues],
  );

  const { handleLoadBottom, firstItemIndex, loadingMoreBottom, loading, nodes, error } =
    useInfiniteListQuery<Pick<Query, 'schedules'>, Partial<QuerySchedulesArgs>, Schedule>(
      {
        queryOptions: {
          query: GET_DASHBOARD_APPOINTMENTS_QUERY,
          getNodeIdCallback: (notification) => notification?.id,
          variables: {
            first: PAGE_SIZE,
            institutionId,
            where: whereInputResolver(appointmentListInput),
          },
          getEdgesCallback: (data) => data?.schedules.edges ?? [],
          getPageInfoCallback: (data) => data?.schedules.pageInfo,
          onError: handleGraphQLError,
          queryPageSize: PAGE_SIZE,
        },
      },
    );

  const isEmpty = nodes.length === 0 || !!error;

  const createPriorityIconRenderer = useCallback(
    (appointment: Schedule) => (
      <PriorityPickerContainer>
        {CustomPriorityIcon(appointment.priority)}
      </PriorityPickerContainer>
    ),
    [],
  );

  const items = useMemo(
    () =>
      mapTaskOrAppointmentEdgesToListItemData(
        nodes,
        'startAt',
        'user',
        createPriorityIconRenderer,
        'appointment',
      ),
    [createPriorityIconRenderer, nodes],
  );

  const cardFilters = useMemo(
    () => (
      <ChipFilters<EventFilterValue>
        options={[
          {
            label: t('common:pointInTime.today'),
            testId: `${BASE_TEST_ID}-filter-today`,
            value: EventFilterValue.TODAY,
          },
          {
            label: t('common:upcoming'),
            testId: `${BASE_TEST_ID}-filter-upcoming`,
            value: EventFilterValue.UPCOMING,
          },
        ]}
        selectedOption={activeFilter}
        onChanged={setActiveFilter}
      />
    ),
    [activeFilter, t],
  );

  const emptyStateKey =
    activeFilter === EventFilterValue.TODAY
      ? 'noAppointmentsForToday'
      : 'noUpcomingAppointments';

  return (
    <TaskOrAppointmentList
      title={t('dashboard:cards.nextAppointments.title')}
      titleExtraContent={cardFilters}
      items={items}
      loading={loading}
      isEmpty={isEmpty}
      emptyStateIcon="schedule"
      emptyStateMessage={t(`dashboard:cards.nextAppointments.${emptyStateKey}`)}
      dateLabel={t('dashboard:cards.nextAppointments.date')}
      linkTo={`/i/company/${institutionId}/cms/appointments?${getLegacyFiltersQueryString(
        appointmentListQueryStringInput,
      )}`}
      linkText={t('common:viewMore')}
      testId={BASE_TEST_ID}
      cardInfoI18nKey="dashboard:cards.nextAppointments.tooltipDescription"
      handleLoadBottom={handleLoadBottom}
      firstItemIndex={firstItemIndex}
      loadingMoreBottom={loadingMoreBottom}
    />
  );
};
